/**
 * Canonical HubSpot event names. Add new user-facing actions here first,
 * then call `trackEvent(ANALYTICS_EVENTS.X, {...})` at the call site — this
 * keeps names stable/typo-free and gives one place to see full app coverage.
 * See AGENTS.md "HubSpot analytics" for the required rule on new features.
 */
export const ANALYTICS_EVENTS = {
  // Auth
  LOGIN_SUBMIT: 'login_submit',
  LOGOUT: 'logout',

  // Navigation
  PAGE_VIEW: 'page_view',
  NAV_EXTERNAL_HUB_CLICK: 'nav_external_hub_click',
  MODE_TAB_SWITCH: 'mode_tab_switch',
  INTELLIGENCE_MODE_SWITCH: 'intelligence_mode_switch',

  // Search
  SEARCH_SUBMIT: 'search_submit',
  SEARCH_CLEAR: 'search_clear',
  SEARCH_MODE_TAG_TOGGLE: 'search_mode_tag_toggle',
  SEARCH_RESULT_CLICK: 'search_result_click',
  SEARCH_SECTION_FILTER: 'search_section_filter',
  SEARCH_ASSET_TYPE_FILTER: 'search_asset_type_filter',
  SEARCH_SOURCE_FILTER: 'search_source_filter',
  SEARCH_ADVANCED_FILTER_APPLY: 'search_advanced_filter_apply',
  SEARCH_ADVANCED_FILTER_CLEAR: 'search_advanced_filter_clear',
  SEARCH_FILTER_PANEL_TOGGLE: 'search_filter_panel_toggle',

  // Media library / grid
  MEDIA_TILE_CLICK: 'media_tile_click',
  MEDIA_TILE_PLAY: 'media_tile_play',
  MEDIA_TILE_SELECT: 'media_tile_select',
  MEDIA_TILE_ADD_TO_COLLECTION: 'media_tile_add_to_collection',
  MEDIA_TILE_REMOVE_FROM_COLLECTION: 'media_tile_remove_from_collection',
  MEDIA_TILE_DELETE: 'media_tile_delete',
  CONTENT_ROW_TOGGLE: 'content_row_toggle',
  CONTENT_ROW_VIEW_ALL: 'content_row_view_all',
  TREND_ADAPT: 'trend_adapt',
  TREND_FILTER: 'trend_filter',

  // Asset detail drawer
  DRAWER_OPEN: 'drawer_open',
  DRAWER_CLOSE: 'drawer_close',
  VIDEO_PLAY: 'video_play',
  VIDEO_PAUSE: 'video_pause',
  VIDEO_SEEK_TRANSCRIPT: 'video_seek_transcript',
  DOWNLOAD_CLICK: 'download_click',
  COPY_LINK: 'copy_link',
  SHARE_CLICK: 'share_click',
  ACCORDION_TOGGLE: 'accordion_toggle',
  DRAWER_SEARCH: 'drawer_search',

  // Tags / notes
  TAG_ADD: 'tag_add',
  TAG_EDIT: 'tag_edit',
  TAG_DELETE: 'tag_delete',
  TAG_CLICK: 'tag_click',
  NOTE_ADD: 'note_add',
  NOTE_DELETE: 'note_delete',

  // Campaigns
  CAMPAIGN_MENU_OPEN: 'campaign_menu_open',
  CAMPAIGN_ASSIGN: 'campaign_assign',
  CAMPAIGN_CREATE: 'campaign_create',
  OPEN_IN_APP: 'open_in_app',

  // Sections / collections
  SECTION_CREATE: 'section_create',
  SECTION_DELETE: 'section_delete',
  SECTION_ASSIGN: 'section_assign',
  CONTENT_ENRICHMENT_OPEN: 'content_enrichment_open',
  CLIP_SELECTION_ADD_TO_COLLECTION: 'clip_selection_add_to_collection',
  CLIP_SELECTION_CLEAR: 'clip_selection_clear',

  // Upload
  UPLOAD_MODAL_OPEN: 'upload_modal_open',
  UPLOAD_FILE_SELECTED: 'upload_file_selected',
  UPLOAD_START: 'upload_start',
  UPLOAD_COMPLETE: 'upload_complete',
  UPLOAD_FAIL: 'upload_fail',
  UPLOAD_CANCEL: 'upload_cancel',
  UPLOAD_RETRY: 'upload_retry',

  // Video player (shared component)
  PLAYER_PLAY_PAUSE: 'player_play_pause',
  PLAYER_SCRUB: 'player_scrub',
  PLAYER_SKIP: 'player_skip',
  PLAYER_MUTE_TOGGLE: 'player_mute_toggle',
  PLAYER_FULLSCREEN_TOGGLE: 'player_fullscreen_toggle',

  // Document intelligence
  DOCUMENT_PASSAGE_SEARCH: 'document_passage_search',
  DOCUMENT_PASSAGE_JUMP: 'document_passage_jump',
} as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];
